import { useContext } from "react";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { rem } from "@mantine/core";
import { useForm, UseFormReturnType, zodResolver } from "@mantine/form";

import { Loading } from "@/components/Loading";
import { EncounterContext } from "@/contexts/encounter";
import { PatientContext } from "@/contexts/patient";
import { NewbornPostpartumPhysicalExamTypes } from "@/fhir/enums/newborn-postpartum-physical-exams";
import { NewbornPostpartumPhysicalExamsQuestionnaires } from "@/fhir/enums/questionnaires";
import usePhysicalExamInitialValues from "@/physical-exams/hooks/use-physical-exam-initial-values";
import usePhysicalExamQuestionnaire from "@/physical-exams/hooks/use-physical-exam-questionnaire";
import usePhysicalExamUnsavedChanges from "@/physical-exams/hooks/usePhysicalExamUnsavedChanges";
import PhysicalExamHeader from "@/physical-exams/PhysicalExamHeader";
import PhysicalExamList from "@/physical-exams/PhysicalExamList";
import {
  amendPhysicalExamQuestionnaireResponse,
  createPhysicalExamQuestionnaireResponse,
  getMainQuestionnaireUrlFromEncounter,
} from "@/physical-exams/services/physical-exams";
import { PhysicalExamProps } from "@/physical-exams/types/physical-exams";
import { PhysicalExamForm } from "@/physical-exams/zod-schemas/physical-exams";
import { useTemporarySubmit } from "@/utils/hooks/use-temporary-submit";

import { HEAD_FORM_INITIAL_VALUES } from "../../types/newborn-postpartum-head";
import {
  NewbornPostpartumPhysicalExam,
  NewbornPostpartumPhysicalExamSchema,
} from "../../zod-schemas/newborn-postpartum-physical-exam";

export default function NewbornPostpartumPhysicalExamHead({
  url,
  submitTrigger,
  editable,
  isAmendment,
}: PhysicalExamProps) {
  const { patient } = useContext(PatientContext);
  const { encounter } = useContext(EncounterContext);
  const { sections, questionnaire } = usePhysicalExamQuestionnaire(
    NewbornPostpartumPhysicalExamsQuestionnaires.Head,
  );
  const medplum = useMedplum();
  const form = useForm<NewbornPostpartumPhysicalExam>({
    initialValues: HEAD_FORM_INITIAL_VALUES,
    validate: zodResolver(NewbornPostpartumPhysicalExamSchema),
  });

  const formSetValues = form.setValues;

  const [initialValues, questionnaireResponse, isLoading] =
    usePhysicalExamInitialValues<NewbornPostpartumPhysicalExam>(
      questionnaire?.id,
      encounter,
      formSetValues,
      HEAD_FORM_INITIAL_VALUES,
    );
  const { markAsSaved } = usePhysicalExamUnsavedChanges(url, form, initialValues);
  const practitioner = medplum.getProfile() as Practitioner;

  const handleSubmit = async (values: NewbornPostpartumPhysicalExam) => {
    const mainQuestionnaireUrl = getMainQuestionnaireUrlFromEncounter(encounter!);
    // If questionnaireResponse is undefined, then the amend action shouldn't happen
    // since the PE answers are being created at this point.
    if (isAmendment && questionnaireResponse) {
      await amendPhysicalExamQuestionnaireResponse<NewbornPostpartumPhysicalExam>(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    } else {
      await createPhysicalExamQuestionnaireResponse<NewbornPostpartumPhysicalExam>(
        medplum,
        questionnaire,
        createReference(encounter!),
        createReference(practitioner),
        patient!.id!,
        initialValues,
        values,
        mainQuestionnaireUrl,
      );
    }
    markAsSaved();
  };

  useTemporarySubmit<NewbornPostpartumPhysicalExam>(form, submitTrigger, handleSubmit);

  if (!patient?.id || isLoading) return <Loading height={rem(96)} />;

  return (
    <form
      data-testid="physical-exam-head-form"
      onSubmit={form.onSubmit((values, _) => handleSubmit(values))}
    >
      <PhysicalExamHeader
        url={url}
        title="Head"
        canDelete={editable}
        clientId={patient!.id!}
        showQuickSelection={editable}
        value={NewbornPostpartumPhysicalExamTypes.Head}
        form={form as UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>}
      >
        <PhysicalExamList
          hideDetails={true}
          sections={sections}
          editable={editable}
          hideGeneralNotes={true}
          questionnaire={questionnaire}
          form={form as UseFormReturnType<PhysicalExamForm | NewbornPostpartumPhysicalExam>}
        />
      </PhysicalExamHeader>
    </form>
  );
}
